import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, BarChart3, Wrench, MapPin, Award, Phone, Mail, Loader2 } from "lucide-react";
import AdminQuotesTrades from "./AdminQuotesTrades";

const ST_FR = { pending_pro: "Recherche en cours", distributed: "Transmise", closed: "Clôturée", suspended: "Suspendue" };
const AS_FR = { proposed: "Proposée", accepted: "Acceptée", refused: "Refusée", expired: "Expirée" };

export default function AdminQuotes() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [founders, setFounders] = useState([]);
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState("");
  const [opp, setOpp] = useState(null);
  const [oppCfg, setOppCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);

  const loadStats = () => api.get("/devis/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  const loadRequests = () => api.get(`/devis/admin/requests${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setRequests(r.data.items || [])).catch(() => {});
  const loadFounders = () => api.get("/devis/admin/founders").then((r) => setFounders(r.data.items || [])).catch(() => {});

  useEffect(() => { loadStats(); loadRequests(); loadFounders(); api.get("/devis/admin/opportunities/stats").then((r) => setOpp(r.data)).catch(() => {}); api.get("/devis/admin/opportunities/config").then((r) => setOppCfg(r.data)).catch(() => {}); }, []);

  const saveOppCfg = async () => {
    setSavingCfg(true);
    try {
      const { data } = await api.patch("/devis/admin/opportunities/config", oppCfg);
      setOppCfg(data); toast.success("Réglages des opportunités enregistrés.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingCfg(false); }
  };

  const openDetail = (id) => api.get(`/devis/admin/requests/${id}`).then((r) => setDetail(r.data)).catch((e) => toast.error(formatApiError(e)));
  const extend = async (bid) => {
    if (!window.confirm("Prolonger la gratuité de 12 mois pour ce professionnel ?")) return;
    try { await api.post(`/devis/admin/founders/${bid}/extend`, { months: 12 }); toast.success("Gratuité prolongée de 12 mois."); loadFounders(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const suspend = async (id) => {
    if (!window.confirm("Suspendre définitivement cette demande ? Elle ne sera plus visible.")) return;
    try { await api.post(`/devis/admin/requests/${id}/suspend`); toast.success("Demande suspendue."); loadRequests(); setDetail(null); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const TABS = [["overview", "Vue d'ensemble"], ["requests", "Demandes"], ["trades", "Métiers"], ["founders", "Pros Fondateurs"]];

  return (
    <div data-testid="admin-quotes-page">
      <h1 className="font-heading text-2xl font-extrabold text-[#172A3A] mb-1">Demandes de devis locaux</h1>
      <p className="text-sm text-slate-500 mb-5">Pilotage du système de mise en relation particuliers ↔ professionnels.</p>

      {opp && (
        <div className="mb-6 rounded-2xl border border-orange-200 bg-orange-50/50 p-4" data-testid="admin-opp-stats">
          <div className="text-sm font-bold text-[#E05638] uppercase tracking-wide mb-3">Espace Opportunités</div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[["Opportunités ouvertes", opp.open_opportunities], ["Demandes sauvées", opp.saved_requests], ["Prises via l'espace", opp.opportunity_responses], ["Demandes expirées", opp.expired_requests], ["Taux de sauvetage", `${opp.saved_rate_pct}%`]].map(([k, v]) => (
              <div key={k} className="bg-white rounded-xl border border-orange-100 p-3"><div className="text-xl font-extrabold text-[#172A3A]">{v}</div><div className="text-[11px] text-slate-500">{k}</div></div>
            ))}
          </div>
          {oppCfg && (
            <div className="mt-4 pt-4 border-t border-orange-200" data-testid="admin-opp-config">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">Réglages (sans toucher au code)</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {[["min_age_days", "Ancienneté minimale (jours)", "Une demande apparaît après ce délai"],
                  ["max_proposals", "Nb max de propositions", "Au-delà, la demande quitte l'espace"],
                  ["relance_days", "Relance intelligente (jours)", "E-mail d'ajustement zone/budget avant expiration"],
                  ["expire_days", "Expiration sans réponse (jours)", "Clôture + relance du particulier"]].map(([key, label, hint]) => (
                  <label key={key} className="block bg-white rounded-xl border border-orange-100 p-3">
                    <span className="text-xs font-semibold text-slate-700">{label}</span>
                    <input type="number" min={key === "max_proposals" ? 1 : 0} value={oppCfg[key] ?? ""}
                      onChange={(e) => setOppCfg((c) => ({ ...c, [key]: e.target.value === "" ? "" : parseInt(e.target.value, 10) }))}
                      data-testid={`admin-opp-cfg-${key}`}
                      className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#E05638]" />
                    <span className="text-[11px] text-slate-400">{hint}</span>
                  </label>
                ))}
              </div>
              <button onClick={saveOppCfg} disabled={savingCfg} data-testid="admin-opp-cfg-save"
                className="mt-3 inline-flex items-center gap-2 h-10 px-5 rounded-xl bg-[#172A3A] text-white text-sm font-semibold hover:bg-[#223c52] disabled:opacity-50">
                {savingCfg ? <Loader2 size={15} className="animate-spin" /> : null} Enregistrer les réglages
              </button>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 mb-6 border-b border-slate-200">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-quotes-tab-${k}`}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-[#E05638] text-[#E05638]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{label}</button>
        ))}
      </div>

      {tab === "overview" && (stats ? (
        <div data-testid="admin-quotes-overview">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
            {[["Demandes", stats.total], ["Distribuées", stats.distributed], ["Sans pro", stats.orphans], ["Pros actifs", stats.active_pros], ["Réponses", stats.responses]].map(([k, v]) => (
              <div key={k} className="bg-white rounded-xl border border-slate-100 p-4"><div className="text-2xl font-extrabold text-[#172A3A]">{v}</div><div className="text-xs text-slate-500">{k}</div></div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-slate-100 p-5">
              <h3 className="font-bold text-[#172A3A] mb-3 flex items-center gap-2"><Wrench size={16} className="text-[#E05638]" /> Métiers les plus demandés</h3>
              {stats.top_trades.length === 0 ? <p className="text-sm text-slate-400">Aucune donnée.</p> : stats.top_trades.map((t) => (
                <div key={t.name} className="flex justify-between text-sm py-1 border-b border-slate-50 last:border-0"><span className="text-slate-700">{t.name}</span><span className="font-bold">{t.count}</span></div>
              ))}
            </div>
            <div className="bg-white rounded-2xl border border-slate-100 p-5">
              <h3 className="font-bold text-[#172A3A] mb-3 flex items-center gap-2"><MapPin size={16} className="text-[#E05638]" /> Communes les plus actives</h3>
              {stats.top_cities.length === 0 ? <p className="text-sm text-slate-400">Aucune donnée.</p> : stats.top_cities.map((c) => (
                <div key={c.name} className="flex justify-between text-sm py-1 border-b border-slate-50 last:border-0"><span className="text-slate-700">{c.name}</span><span className="font-bold">{c.count}</span></div>
              ))}
            </div>
          </div>
        </div>
      ) : <div className="p-10 text-center text-slate-400"><Loader2 className="animate-spin inline" /></div>)}

      {tab === "requests" && (
        <div data-testid="admin-quotes-requests">
          <div className="flex gap-2 mb-4">
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadRequests()} placeholder="Rechercher (réf, ville, e-mail…)" className="flex-1 h-10 px-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-[#E05638]" data-testid="admin-quotes-search" />
            <button onClick={loadRequests} className="px-4 py-2 rounded-xl bg-[#172A3A] text-white text-sm font-semibold">Rechercher</button>
          </div>
          {requests.length === 0 ? <div className="p-10 text-center text-slate-400"><Inbox size={30} className="mx-auto mb-2" />Aucune demande.</div> : (
            <div className="space-y-2">
              {requests.map((r) => (
                <button key={r.id} onClick={() => openDetail(r.id)} className="w-full text-left bg-white rounded-xl border border-slate-100 p-4 hover:border-[#E05638] transition-colors" data-testid="admin-quote-row">
                  <div className="flex justify-between flex-wrap gap-2">
                    <div><span className="font-bold text-[#172A3A]">{r.trade_name}</span> <span className="text-sm text-slate-500">· {r.city} · {r.ref}</span></div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{ST_FR[r.status] || r.status} · {r.assigned_count} pro(s)</span>
                  </div>
                  <div className="text-sm text-slate-500 mt-1 line-clamp-1">{r.description}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "founders" && (
        <div data-testid="admin-quotes-founders">
          {founders.length === 0 ? <div className="p-10 text-center text-slate-400">Aucun professionnel inscrit.</div> : (
            <div className="space-y-2">
              {founders.map((f) => (
                <div key={f.business_id} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center justify-between flex-wrap gap-2" data-testid="admin-founder-row">
                  <div>
                    <div className="font-semibold text-[#172A3A] flex items-center gap-1.5"><Award size={14} className="text-[#E05638]" /> {f.business_name || f.business_id}</div>
                    <div className="text-xs text-slate-500">{f.business_city} · gratuit jusqu'au {f.free_until ? new Date(f.free_until).toLocaleDateString("fr-FR") : "—"} · {f.requests_received || 0} demandes reçues · {f.responses_sent || 0} réponses</div>
                  </div>
                  <button onClick={() => extend(f.business_id)} className="px-3 py-1.5 rounded-lg text-sm font-semibold text-[#E05638] border border-orange-200 hover:bg-orange-50" data-testid="admin-founder-extend">+ 12 mois gratuits</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "trades" && <AdminQuotesTrades />}

      {/* Détail demande */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="admin-quote-detail">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDetail(null)} />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[85vh] overflow-y-auto p-6">
            <div className="flex justify-between items-start">
              <h3 className="font-heading text-xl font-bold text-[#172A3A]">{detail.request.trade_name} · {detail.request.ref}</h3>
              <button onClick={() => setDetail(null)} className="text-slate-400 text-xl">×</button>
            </div>
            <p className="mt-2 text-sm text-slate-700 whitespace-pre-line">{detail.request.description}</p>
            <div className="mt-3 flex flex-wrap gap-3 text-sm p-3 rounded-xl bg-[#F2F4F5]">
              <span className="font-semibold">{detail.request.first_name} {detail.request.last_name}</span>
              <a href={`tel:${detail.request.phone}`} className="inline-flex items-center gap-1 text-[#E05638]"><Phone size={13} /> {detail.request.phone}</a>
              <a href={`mailto:${detail.request.email}`} className="inline-flex items-center gap-1 text-slate-600"><Mail size={13} /> {detail.request.email}</a>
            </div>
            <h4 className="font-bold text-[#172A3A] mt-4 mb-1 text-sm">Professionnels attribués</h4>
            {detail.assignments.map((a) => (
              <div key={a.id} className="text-sm flex justify-between py-1 border-b border-slate-50"><span>{a.business_name} ({a.business_city})</span><span className="text-slate-500">score {a.score} · {AS_FR[a.status] || a.status}</span></div>
            ))}
            <h4 className="font-bold text-[#172A3A] mt-4 mb-1 text-sm">Réponses</h4>
            {detail.responses.length === 0 ? <p className="text-sm text-slate-400">Aucune.</p> : detail.responses.map((r, i) => (
              <div key={i} className="text-sm py-1.5 border-b border-slate-50"><span className="text-slate-700">{r.message}</span>{r.price_estimate && <span className="text-emerald-700 font-semibold"> · {r.price_estimate}</span>}</div>
            ))}
            <button onClick={() => suspend(detail.request.id)} className="mt-4 px-4 py-2 rounded-xl text-sm font-semibold text-red-600 border border-red-200 hover:bg-red-50" data-testid="admin-quote-suspend">Suspendre cette demande</button>
          </div>
        </div>
      )}
    </div>
  );
}
